/**
 * Filters undefined, null or empty strings as well as boolean false from a list of comma separated items, joins them and returns a string
 * Returns null if 'classes' is empty
 * @param {object} classes - An arguments object
 */
export const setClassNames = (...classes) => {
	return [...classes].filter(className => className).join(' ') || null;
};

/**
 * Copy postfixed preference values to their un-postfixed keys in a preference object.
 * E.g. copy the value of 'titleHeadingColor-sidebar' to 'titleHeadingColor' and remove
 * 	'titleHeadingColor-sidebar' from the object.
 * @param {object} prefsObj - An object of { preference: value, ... }
 * @param {string} modifier - An identifier postfixed to differentiate preferences (e.g. '-sidebar')
 */
export const remapPrefs = (prefsObj, modifier) => {
	const newPrefs = { ...prefsObj };

	// Extract preferences postfixed with modifier
	Object.keys(newPrefs)
		.filter(pref => pref.includes(modifier) && newPrefs[pref] !== '')
		.forEach(pref => {
			newPrefs[pref.replace(modifier, '')] = prefsObj[pref];
		});

	// Remove postfixed preferences and copy new pref values over the original ones
	const filteredPrefs = Object.keys(newPrefs)
		.filter(key => !key.includes(modifier))
		.reduce(
			(obj, key) => ({
				...obj,
				[key]: newPrefs[key]
			}),
			{}
		);

	return filteredPrefs;
};

/**
 * Create formatted search request object
 * @param {string} vehicleId
 * @param {string} accountId
 * @returns {object} - Formatted search request object
 */
export const getSearchRO = (vehicleId, accountId, subTitle) => {
	const searchRO = {
		vehicle: {
			rql: `&select(type,make,year,model,trim,bodyStyle,stockNumber,certified,normalFuelType)&eq(uuid,${vehicleId})`
		}
	};

	if (subTitle) {
		searchRO.accounts = {
			rql: `&select(address)&eq(accountId,${accountId})`
		};
	}

	return searchRO;
};

/**
 * replaces custom tags [city] and [state] with values from account address
 * @param {string} subTitleTemplate - preference value with SEO tags
 * @param {object} accountAddress - from vcda query
 * @returns string
 */
export const replaceSeoTags = (subTitleTemplate, accountAddress) => {
	const { city, state } = accountAddress;

	let subTitle = subTitleTemplate.replace(/\[city\]/gi, city); // Replace city
	subTitle = subTitle.replace(/\[state\]/gi, state); // Replace the state

	return subTitle;
};

export const getAccountId = rData => {
	return (
		(rData.parameterMap && rData.parameterMap.accountId) || rData.accountId
	);
};

/**
 * take array of title attributes from Item Display Config, and returns array of strings split
 * on lineBreak virtual attribute to handle title and subtitle display.
 * @param {object} attrs - array of attribute objects returned from item display config service
 * @param {object} labels - i18n labels loaded by widget from labels.json
 * @param {string} type - vehicle type attribue (new/used)
 * @param {string} isCertified - vehicle certified attribute
 * @param {string} forceShowVehicleCondition - pref value
 * @returns array of strings
 */
export const getVehicleTitle = (
	attrs,
	labels,
	type,
	isCertified,
	forceShowVehicleCondition
) => {
	const NEXT_LINE = '\u0085';

	// TODO: Remove forceLineBreak once TITLE configs have been updated for all sites
	let forceLineBreak = false;
	if (!attrs.some(e => e.name === 'lineBreak')) {
		forceLineBreak = true;
	}

	const vehicleTitleAttrs = attrs.reduce((result, { name, itemValue }) => {
		let attr = itemValue;
		if (name === 'lineBreak') {
			attr = NEXT_LINE;
		}
		result.push(attr);

		// TODO: Remove forceLineBreak once TITLE configs have been updated for all sites
		if (forceLineBreak && name === 'make') {
			result.push(NEXT_LINE);
		}

		return result;
	}, []);

	// TODO: Remove this forceVehcileType override when we have a better solution
	if (
		forceShowVehicleCondition === 'true' &&
		!attrs.some(e => e.name === 'type')
	) {
		const vehicleType = isCertified
			? 'CERTIFIED_PRE_OWNED'
			: type.toUpperCase();
		vehicleTitleAttrs.unshift(labels.get(vehicleType));
	}

	return vehicleTitleAttrs
		.join(' ')
		.split(NEXT_LINE)
		.map(s => s.trim());
};
